<template>
    <v-dialog
        :value="isOpen"
        max-width="800px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    <template v-if="!isNewQualitativeNote">
                        Podgląd uwagi jakościowej
                        <template v-if="isJob">
                            <strong>(Zlecenie {{ qualitativeNote.reportItemNumber }} - Montaż {{ qualitativeNote.plateNumber }})</strong>
                        </template>
                        <template v-else>
                            <strong>(Zamówienie {{ qualitativeNote.reportItemNumber }})</strong>
                        </template>
                    </template>
                    <template v-else>
                        Tworzenie uwagi jakościowej
                        <template v-if="isJob">
                            <strong>(Zlecenie {{ reportItemPlate.reportItemPrimaryNumber }} - Montaż {{ reportItemPlate.plateNumber }})</strong>
                        </template>
                        <template v-else>
                            <strong>(Zamówienie {{ reportItemPlate.reportItemPrimaryNumber }})</strong>
                        </template>
                    </template>
                </h1>
            </v-card-title>

            <v-card-text>
                <v-row align="center">
                    <v-col cols="12" sm="12" lg="12">
                        <v-autocomplete
                            v-model="qualitativeNote.reportedById"
                            label="Zgłaszający"
                            :items="users"
                            :filter="customUsersFilter"
                            :loading="usersSearchLoading"
                            :search-input.sync="usersSearch"
                            class="v-text-field--placeholder-dark"
                            required
                            dense
                            outlined
                            item-value="id"
                            no-data-text="Nie znaleziono"
                            clearable
                            :error-messages="reportedByIdErrors"
                            :disabled="!isNewQualitativeNote"
                        >
                            <template slot="no-data">
                                <v-list-item>
                                    <v-list-item-title>
                                        <template v-if="!usersSearch">
                                            Wpisz treść aby odnaleźć <strong>użytkownika</strong>.
                                        </template>
                                        <template v-else-if="usersSearchLoading">
                                            Trwa wyszukiwanie...
                                        </template>
                                        <template v-else>
                                            Nie znaleziono
                                        </template>
                                    </v-list-item-title>
                                </v-list-item>
                            </template>
                            <template slot="selection" slot-scope="data">
                                <template v-if="data.item.firstName && data.item.lastName">
                                    {{ data.item.firstName }} {{ data.item.lastName }}
                                </template>
                                <template v-else>
                                    {{ data.item.username }}
                                </template>
                            </template>
                            <template slot="item" slot-scope="data">
                                <template v-if="data.item.firstName && data.item.lastName">
                                    {{ data.item.firstName }} {{ data.item.lastName }}
                                </template>
                                <template v-else>
                                    {{ data.item.username }}
                                </template>
                            </template>
                        </v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="12" lg="12">
                        <v-autocomplete
                            v-model="qualitativeNote.responsiblePeople"
                            label="Osoby odpowiedzialne"
                            :items="responsibleUsers"
                            :filter="customUsersFilter"
                            :loading="responsibleUsersSearchLoading"
                            :search-input.sync="responsibleUsersSearch"
                            class="v-text-field--placeholder-dark"
                            required
                            outlined
                            multiple
                            chips
                            small-chips
                            deletable-chips
                            cache-items
                            item-value="id"
                            no-data-text="Nie znaleziono"
                            clearable
                            :error-messages="responsiblePeopleErrors"
                            :disabled="!isNewQualitativeNote"
                            @change="responsiblePeopleChanged"
                        >
                            <template slot="no-data">
                                <v-list-item>
                                    <v-list-item-title>
                                        <template v-if="!responsibleUsersSearchLoading">
                                            Wpisz treść aby odnaleźć <strong>osobę odpowiedzialną</strong>.
                                        </template>
                                        <template v-else-if="responsibleUsersSearchLoading">
                                            Trwa wyszukiwanie...
                                        </template>
                                        <template v-else>
                                            Nie znaleziono
                                        </template>
                                    </v-list-item-title>
                                </v-list-item>
                            </template>

                            <template #selection="data">
                                <template v-if="data.item.user">
                                    <template v-if="data.item.user.firstName && data.item.user.lastName">
                                        <v-chip
                                            close
                                            @update:active="removeUser(data.item)"
                                        >
                                            {{ data.item.user.firstName }} {{ data.item.user.lastName }}
                                        </v-chip>
                                    </template>
                                    <template v-else>
                                        <v-chip
                                            close
                                            @update:active="removeUser(data.item)"
                                        >
                                            {{ data.item.user.username }}
                                        </v-chip>
                                    </template>
                                </template>
                                <template v-else>
                                    <template v-if="data.item.firstName && data.item.lastName">
                                        <v-chip
                                            close
                                            @update:active="removeUser(data.item)"
                                        >
                                            {{ data.item.firstName }} {{ data.item.lastName }}
                                        </v-chip>
                                    </template>
                                    <template v-else>
                                        <v-chip
                                            close
                                            @update:active="removeUser(data.item)"
                                        >
                                            {{ data.item.username }}
                                        </v-chip>
                                    </template>
                                </template>
                            </template>

                            <template #item="data">
                                <template v-if="data.item.firstName && data.item.lastName">
                                    {{ data.item.firstName }} {{ data.item.lastName }}
                                </template>
                                <template v-else>
                                    {{ data.item.username }}
                                </template>
                            </template>
                        </v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="6" lg="6">
                        <v-select
                            v-model="qualitativeNote.productionProcessId"
                            :items="productionProcesses"
                            label="Proces produkcyjny"
                            item-value="id"
                            item-text="name"
                            no-data-text="Nie znaleziono procesu produkcyjnego"
                            outlined
                            dense
                            :error-messages="productionProcessIdErrors"
                            :disabled="!isNewQualitativeNote"
                        />
                    </v-col>

                    <v-col cols="12" sm="6" lg="6">
                        <v-select
                            v-model="qualitativeNote.codeId"
                            :items="codes"
                            label="Defekt"
                            item-value="id"
                            item-text="name"
                            no-data-text="Nie znaleziono defektów"
                            outlined
                            dense
                            :error-messages="codeIdErrors"
                            :disabled="!isNewQualitativeNote"
                        />
                    </v-col>
                    <v-col cols="12" sm="12" lg="12">
                        <v-textarea
                            v-model="qualitativeNote.comment"
                            :hint="qualitativeNoteCommentHint(qualitativeNote.comment)"
                            label="Komentarz"
                            outlined
                            dense
                            auto-grow
                            rows="2"
                            :error-messages="commentErrors"
                            :disabled="!isNewQualitativeNote"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <v-data-table
                            v-if="isJob"
                            v-model="ordersSelected"
                            :headers="headers"
                            :items="reportItemPlateOrders"
                            :loading="reportItemPlateOrdersLoading"
                            :mobile-breakpoint="0"
                            :footer-props="footerProps"
                            :server-items-length="pagination.itemsLength"
                            :page.sync="pagination.page"
                            :items-per-page.sync="pagination.itemsPerPage"
                            show-select
                            name="reportItemPlateOrdersData"
                            @pagination="updatePagination"
                        >
                            <template #header.data-table-select="{on, props}">
                                <v-simple-checkbox
                                    v-model="selectAllChecked"
                                    :ripple="false"
                                    :class="{'selectErrors': ordersSelectedErrors}"
                                    :disabled="!isNewQualitativeNote"
                                    v-bind="props"
                                    v-on="on"
                                    @input="selectAllItems($event)"
                                />
                            </template>
                            <template #item.data-table-select="{item}">
                                <v-simple-checkbox
                                    v-model="item.checked"
                                    :ripple="false"
                                    :class="{'selectErrors': ordersSelectedErrors}"
                                    :disabled="!isNewQualitativeNote"
                                    @input="updateSelectedItems(item, $event)"
                                />
                            </template>
                            <template #item.createdAt="{item}">
                                {{ formatDate(item.createdAt) }}
                            </template>
                        </v-data-table>
                    </v-col>
                </v-row>
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Zamknij"
                    @click="onClose"
                >
                    Zamknij
                </v-btn>
                <v-spacer />
                <v-btn
                    v-if="isNewQualitativeNote"
                    color="success"
                    text
                    data-sentry-label="Zapisz"
                    :loading="actionLoading"
                    @click="onSave()"
                >
                    Zapisz
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import customFilters from '@/mixins/customFilters'
import filters from '@/mixins/dataTableFilters'
import pagination from '@/mixins/dataTablePagination'
import dateFormatter from '@/mixins/dateFormatter'
import tooltipDelay from '@/mixins/tooltip'
import { required, maxLength } from 'vuelidate/lib/validators'
import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'
import { isEmpty } from 'lodash'
import { EventBus } from '@/utils/eventBus'

let searchTimer

export default {
    name: 'QualitativeNoteDialog',
    mixins: [
        tooltipDelay, pagination, filters, customFilters, dateFormatter
    ],
    props: {
        isOpen: {
            type: Boolean
        },
        qualitativeNoteData: {
            type: Object,
            default: () => {}
        },
        reportItemPlateData: {
            type: Object,
            default: () => {}
        },
        reportItemTypeId: {
            type: Number,
            default: null
        },
        reportItemPlateId: {
            type: Number,
            default: null
        },
        reopenReportItemsDialog: {
            type: Boolean
        },
        qualitativeNoteId: {
            type: Number,
            default: null
        },
        stopCardId: {
            type: Number,
            default: null
        },
        productionProcessId: {
            type: Number,
            default: null
        },
    },
    data() {
        return {
            selectAllChecked: false,
            reportItemPlate: {
                plateNumber: null,
                reportItemNumber: null
            },
            qualitativeNote: {
                reportedById: null,
                productionProcessId: null,
                responsiblePeople: [],
                codeId: null,
                ordersNumbers: [],
                plateNumber: null,
                reportItemPlateId: null,
                comment: null,
                reportedBy: null,
                responsiblePerson: null,
            },
            usersSearch: null,
            usersSearchLoading: false,
            responsibleUsersSearch: null,
            responsibleUsersSearchLoading: false,
            users: [],
            responsibleUsers: [],
            ordersSelected: [],
            ordersSelectedErrors: false,
            reopenReportItems: false,
            actionLoading: false,
            headers: [
                {
                    text: 'Numer',
                    sortable: false,
                    value: 'orderNumber',

                },
                {
                    text: 'Produkt',
                    sortable: false,
                    value: 'productName',

                },
                {
                    text: 'Nakład',
                    sortable: false,
                    value: 'orderAmount',

                }
            ],
        }
    },
    computed: {
        isNewQualitativeNote() {
            return isEmpty(this.qualitativeNoteData)
        },
        productionProcesses() {
            return this.$store.state.configurations.productProductionProcesses.productionProcesses
        },
        codes() {
            return this.$store.state.complaints.qualitativeNotes.codes
        },
        reportItemPlateOrders() {
            return this.$store.state.complaints.qualitativeNotes.reportItemPlateOrders
        },
        reportItemPlateOrdersLoading() {
            return this.$store.state.complaints.qualitativeNotes.reportItemPlateOrdersLoading
        },
        isJob() {
            return [10, 20, 30].includes(this.reportItemTypeId)
        },
        filterQuery() {
            const filters = Object.assign({}, this.filters)
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        },
        reportedByIdErrors() {
            const errors = []

            if (!this.$v.qualitativeNote.reportedById.$dirty) return errors
            !this.$v.qualitativeNote.reportedById.required && errors.push('Proszę wybrać zgłaszającego.')

            return errors
        },
        responsiblePeopleErrors() {
            const errors = []

            if (!this.$v.qualitativeNote.responsiblePeople.$dirty) return errors
            !this.$v.qualitativeNote.responsiblePeople.required && errors.push('Proszę wybrać osobę odpowiedzialną.')
            !this.$v.qualitativeNote.responsiblePeople.maxLength && errors.push('Maksymalnie 10 osób odpowiedzialnych.')

            return errors
        },
        productionProcessIdErrors() {
            const errors = []

            if (!this.$v.qualitativeNote.productionProcessId.$dirty) return errors
            !this.$v.qualitativeNote.productionProcessId.required && errors.push('Proszę wybrać proces.')

            return errors
        },
        codeIdErrors() {
            const errors = []

            if (!this.$v.qualitativeNote.codeId.$dirty) return errors
            !this.$v.qualitativeNote.codeId.required && errors.push('Proszę wybrać kod.')

            return errors
        },
        commentErrors() {
            const errors = []

            if (!this.$v.qualitativeNote.comment.$dirty) return errors
            !this.$v.qualitativeNote.comment.maxLength && errors.push('Maksymalna długość komentarza to 60 znaków.')

            return errors
        }
    },
    watch: {
        isOpen(boolean) {
            if (boolean) {
                this.qualitativeNote = JSON.parse(JSON.stringify(this.qualitativeNoteData))
                this.reportItemPlate = JSON.parse(JSON.stringify(this.reportItemPlateData))
                if (this.qualitativeNote.reportedById) this.users.push(this.qualitativeNote.reportedBy)
                if (this.qualitativeNote.responsiblePeople) {
                    this.qualitativeNote.responsiblePeople.map(people => {
                        this.responsibleUsers.push(people.user)
                    })
                    this.qualitativeNote.responsiblePeople = this.qualitativeNote.responsiblePeople.map(people => people.user)
                }
                if (this.productionProcessId) this.qualitativeNote.productionProcessId = this.productionProcessId
                this.reopenReportItems = JSON.parse(JSON.stringify(this.reopenReportItemsDialog))
                this.pagination.itemsPerPage = 10
                this.pagination.page = 1
                this.getReportItemPlateOrders()
            }
        },
        usersSearch(value = null) {
            if (value === null) return

            this.usersSearchLoading = true

            clearTimeout(searchTimer)

            searchTimer = setTimeout(() => {
                if (value) {
                    this.searchUsers(value)
                } else {
                    this.usersSearchLoading = false
                    this.users = []
                }
            }, 500)
        },
        responsibleUsersSearch(value = null) {
            if (value === null) return

            this.responsibleUsersSearchLoading = true
            clearTimeout(searchTimer)
            searchTimer = setTimeout(() => {
                if (value) {
                    this.searchResponsibleUsers(value)
                } else {
                    this.responsibleUsersSearchLoading = false
                    this.responsibleUsers = []
                }
            }, 500)
        }
    },
    validations() {
        return {
            qualitativeNote: {
                reportedById: {
                    required
                },
                responsiblePeople: {
                    required,
                    maxLength: maxLength(10)
                },
                productionProcessId: {
                    required
                },
                codeId: {
                    required
                },
                comment: {
                    maxLength: maxLength(60)
                }
            },
        }
    },
    methods: {
        responsiblePeopleChanged() {
            this.$v.qualitativeNote.responsiblePeople.$touch()
            if (this.responsibleUsersSearch) {
                this.responsibleUsersSearch = null
            }
        },
        removeUser(user) {
            this.qualitativeNote.responsiblePeople.splice(this.qualitativeNote.responsiblePeople.indexOf(user.id), 1)
        },
        getReportItemPlateOrders() {
            const query = this.isNewQualitativeNote ? `&reportItemPlateId=${this.reportItemPlateId}` : `&qualitativeNoteId=${this.qualitativeNoteId}`

            this.$store.dispatch('complaints/getReportItemPlateOrders', {
                paginate: this.paginate(),
                query
            })
                .then(response => {
                    if (!this.isNewQualitativeNote) {
                        response.data.results.map(item => {
                            item.checked = true
                            this.ordersSelected.push(item)
                        })
                        if ((this.ordersSelected.length === response.data.results.length) && response.data.results.length > 0) this.selectAllChecked = true
                    }
                    if (!this.isJob) this.qualitativeNote.reportItemPlateOrderIds = response.data.results.map(order => order.id)
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        selectAllItems(value) {
            if (value) {
                this.ordersSelectedErrors = false
                this.reportItemPlateOrders.map(item => {
                    item.checked = !item.checked
                })
                this.ordersSelected = JSON.parse(JSON.stringify(this.reportItemPlateOrders))
            } else {
                this.reportItemPlateOrders.map(item => {
                    item.checked = false
                })
                this.ordersSelected = []
                this.selectAllChecked = false
            }
        },
        updateSelectedItems(item, value) {
            if (value) {
                this.ordersSelectedErrors = false
                this.ordersSelected.push(item)
            } else {
                this.ordersSelected.splice(this.ordersSelected.indexOf(item.id), 1)
                this.selectAllChecked = false
            }
        },
        searchUsers(query = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie użytkownika')

            this.$store.dispatch('administration/findActiveUsers', {
                paginate: '?limit=20',
                query: query
            })
                .then(response => {
                    this.users = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.usersSearchLoading = false
                })
        },
        searchResponsibleUsers(query = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie odpowiedzialnego użytkownika')

            this.$store.dispatch('administration/findUsers', {
                paginate: '?limit=20',
                query: query
            })
                .then(response => {
                    this.responsibleUsers = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.responsibleUsersSearchLoading = false
                })
        },
        onSave() {
            this.$v.$touch()
            if (this.isJob) this.ordersSelectedErrors = this.ordersSelected.length === 0

            if (this.$v.$invalid || this.ordersSelectedErrors) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            this.actionLoading = true

            let payload = JSON.parse(JSON.stringify(this.qualitativeNote))

            if (this.isJob) payload.reportItemPlateOrderIds = this.ordersSelected.map(order => order.id)
            payload.reportItemPlateId = this.reportItemPlate.id
            payload.plateNumber = this.reportItemPlate.plateNumber

            if (this.stopCardId) payload.stopCardId = this.stopCardId

            payload.responsiblePeople = payload.responsiblePeople.map(user => ({ userId: user}))

            this.$store.dispatch('complaints/createQualitativeNote', payload)
                .then(({data}) => {
                    this.reopenReportItems = false
                    EventBus.$emit('getQualitativeNotes')
                    this.isNewQualitativeNote && EventBus.$emit('qualitativeNoteCreated', data)
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading = false
                    this.onClose()
                })
        },
        onClose() {
            setTimeout(() => {
                this.qualitativeNote.reportedById = null
                this.qualitativeNote.productionProcessId = null
                this.qualitativeNote.responsiblePeople = []
                this.qualitativeNote.codeId = null
                this.qualitativeNote.ordersNumbers = []
                this.qualitativeNote.plateNumber = null
                this.qualitativeNote.reportItemId = null
                this.users = []
                this.ordersSelected = []
                this.selectAllChecked = false
                this.responsibleUsers = []
            }, 500)
            this.$store.dispatch('complaints/clearReportItemPlateOrders')
            this.ordersSelectedErrors = false
            this.$v.$reset()
            this.resetPagination()
            this.$emit('on-close', this.reopenReportItems)
            if (this.reopenReportItems) EventBus.$emit('showReportItemsDialog')
        },
        updatePagination(pagination) {
            this.pagination = pagination

            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending && (this.reportItemPlateId || this.qualitativeNoteId)) {
                this.getReportItemPlateOrders()
            }
            this.clearPaginationPending()
        },
        qualitativeNoteCommentHint(comment) {
            if (!comment) return
            if (comment.length > 60) return 'Maksymalna długość komentarza to 60 znaków.'
        }
    }
}
</script>